import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from 'axios';
import { useAuth } from '../App';
import { API, getImageUrl } from '../utils';
import { toast } from 'sonner';
import { format, addMonths } from 'date-fns';
import {
  Camera, MapPin, Star, Calendar as CalendarIcon, Clock, MessageCircle,
  CheckCircle, Play, Package, AlertCircle, TrendingUp, BadgeCheck, Award
} from 'lucide-react';

const CreativeProfile = () => {
  const { profileId: profileSlug } = useParams();
  const profileId = profileSlug.startsWith('profile_')
    ? profileSlug
    : `profile_${profileSlug.split('_').pop()}`;
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [servicePackages, setServicePackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioFilter, setPortfolioFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [bookingData, setBookingData] = useState({
    project_title: '',
    project_description: '',
    deliverables: '',
    location: '',
    date: null,
    delivery_date: null,
    time: '10:00',
    duration_hours: 2
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => { fetchProfile(); }, [profileId]);

  useEffect(() => {
    if (profile?.user_id) {
      fetchServicePackages();
      fetchAvailability();
    }
  }, [profile?.user_id]);

  useEffect(() => {
    if (profile) setPortfolioItems(profile.portfolio_items || []);
  }, [profile]);

  const fetchAvailability = async () => {
    if (!profile?.user_id) return;
    try {
      const currentMonth = format(new Date(), 'yyyy-MM');
      const nextMonth = format(addMonths(new Date(), 1), 'yyyy-MM');
      const monthAfter = format(addMonths(new Date(), 2), 'yyyy-MM');
      const [curr, next, after] = await Promise.all([
        axios.get(`${API}/availability/${profile.user_id}?month=${currentMonth}`),
        axios.get(`${API}/availability/${profile.user_id}?month=${nextMonth}`),
        axios.get(`${API}/availability/${profile.user_id}?month=${monthAfter}`)
      ]);
      setAvailabilitySlots([...curr.data, ...next.data, ...after.data]);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const isDateUnavailable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotsForDate = availabilitySlots.filter(s => s.date === dateStr);
    if (slotsForDate.some(s => !s.start_time && !s.is_available)) return true;
    if (slotsForDate.some(s => s.booking_id)) return true;
    return false;
  };

  const isTimeUnavailable = (date, time) => {
    if (!date) return false;
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotsForDate = availabilitySlots.filter(s => s.date === dateStr);
    if (slotsForDate.some(s => !s.start_time && !s.is_available)) return true;
    if (slotsForDate.some(s => s.start_time === time && !s.is_available)) return true;
    if (slotsForDate.some(s => s.start_time === time && s.booking_id)) return true;
    return false;
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/creatives/${profileId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Profile not found');
      navigate('/creatives');
    } finally {
      setLoading(false);
    }
  };

  const fetchServicePackages = async () => {
    try {
      const response = await axios.get(`${API}/service-packages/user/${profile.user_id}`);
      setServicePackages(response.data);
    } catch (error) {
      console.error('Error fetching service packages:', error);
    }
  };

  const selectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setBookingData({
      ...bookingData,
      project_title: pkg.name,
      project_description: '',
      deliverables: pkg.deliverables,
      duration_hours: pkg.duration_hours
    });
    setBookingOpen(true);
  };

  const openHourlyBooking = () => {
    setSelectedPackage(null);
    setBookingData({ project_title: '', project_description: '', deliverables: '', location: '', date: null, delivery_date: null, time: '10:00', duration_hours: 2 });
    setBookingOpen(true);
  };

  const handleBooking = async () => {
    if (!user) { toast.error('Please log in to book'); navigate('/login'); return; }
    if (user.role !== 'business') { toast.error('Only businesses can make bookings'); return; }
    if (!bookingData.project_title || !bookingData.project_description || !bookingData.deliverables || !bookingData.location || !bookingData.date || !bookingData.delivery_date || !bookingData.time || !bookingData.duration_hours || bookingData.duration_hours <= 0) {
      toast.error('Please fill in all required fields'); return;
    }
    setBookingLoading(true);
    try {
      const bookingPayload = {
        creative_id: profile.user_id,
        creative_profile_id: profileId,
        project_title: bookingData.project_title,
        project_description: bookingData.project_description,
        deliverables: bookingData.deliverables,
        location: bookingData.location,
        date: format(bookingData.date, 'yyyy-MM-dd'),
        delivery_date: bookingData.delivery_date ? format(bookingData.delivery_date, 'yyyy-MM-dd') : null,
        time: bookingData.time,
        duration_hours: bookingData.duration_hours
      };
      if (selectedPackage) {
        bookingPayload.package_id = selectedPackage.package_id;
        bookingPayload.package_price = selectedPackage.price;
      }
      await axios.post(`${API}/bookings`, bookingPayload, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Booking request sent!');
      setBookingOpen(false);
      setSelectedPackage(null);
      navigate('/bookings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMessage = () => {
    if (!user) { toast.error('Please log in to message'); navigate('/login'); return; }
    navigate('/messages', { state: { partnerId: profile.user_id } });
  };

  const toggleFeatured = async (itemId) => {
    const item = portfolioItems.find(i => i.item_id === itemId);
    if (!item) return;
    const featuredCount = portfolioItems.filter(i => i.featured).length;
    if (!item.featured && featuredCount >= 5) { toast.error('Maximum 5 featured items allowed'); return; }
    setPortfolioItems(prev => prev.map(i => i.item_id === itemId ? { ...i, featured: !i.featured } : i));
    try {
      await axios.patch(`${API}/creatives/portfolio/${itemId}/featured`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      setPortfolioItems(prev => prev.map(i => i.item_id === itemId ? { ...i, featured: item.featured } : i));
      toast.error('Failed to update featured status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <div className="animate-pulse text-[#a0a0a0] text-sm">Loading profile...</div>
      </div>
    );
  }

  if (!profile) return null;

  const isOwner = user?.user_id === profile?.user_id;
  const featuredItems = portfolioItems.filter(i => i.featured);
  const totalAmount = profile.hourly_rate * bookingData.duration_hours;
  const filteredPortfolioItems = portfolioFilter === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => (item.tags || []).includes(portfolioFilter));

  const portfolioTags = [...new Set(portfolioItems.flatMap(i => i.tags || []))]
    .sort((a, b) => a.localeCompare(b));

  const featuredTags = [...new Set(featuredItems.flatMap(i => i.tags || []))]
    .sort((a, b) => a.localeCompare(b));
  const filteredFeaturedItems = featuredFilter === 'all'
    ? featuredItems
    : featuredItems.filter(item => (item.tags || []).includes(featuredFilter));

  const getYouTubeThumbnail = (url) => {
    const videoId = url.includes('youtu.be') ? url.split('youtu.be/')[1]?.split('?')[0] : url.split('v=')[1]?.split('&')[0];
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
  };

  const getEmbedUrl = (item) => {
    const isYouTube = item.url?.includes('youtube.com') || item.url?.includes('youtu.be');
    const isVimeo = item.url?.includes('vimeo.com');
    if (isYouTube) { const videoId = item.url.includes('youtu.be') ? item.url.split('youtu.be/')[1]?.split('?')[0] : item.url.split('v=')[1]?.split('&')[0]; return `https://www.youtube.com/embed/${videoId}?autoplay=1`; }
    if (isVimeo) { const videoId = item.url.split('vimeo.com/')[1]?.split('?')[0]; return `https://player.vimeo.com/video/${videoId}?autoplay=1`; }
    return item.url;
  };

  const renderPortfolioItem = (item, idx, showStar = false) => {
    const isYouTube = item.url?.includes('youtube.com') || item.url?.includes('youtu.be');
    const isVimeo = item.url?.includes('vimeo.com');
    const isEmbedVideo = isYouTube || isVimeo;
    const isVideo = item.type === 'video' || isEmbedVideo;

    const isPlaying = playingVideoId === (item.item_id || idx);

    return (
      <div key={item.item_id || idx} className="break-inside-avoid mb-2.5 rounded-[14px] overflow-hidden relative cursor-pointer bg-ink group"
        onClick={() => !isVideo && setSelectedImage({ ...item, embedUrl: null, isEmbedVideo: false, type: item.type })}>
        {showStar && isOwner && item.item_id && (
          <button onClick={(e) => { e.stopPropagation(); toggleFeatured(item.item_id); }}
            className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 transition-colors">
            <Star className={`w-4 h-4 ${item.featured ? 'fill-[#d4a843] text-[#d4a843]' : 'text-white'}`} />
          </button>
        )}
        {isVideo ? (
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            {isPlaying ? (
              isYouTube
                ? <iframe src={getEmbedUrl(item)} className="w-full aspect-video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                : isVimeo
                  ? <iframe src={getEmbedUrl(item)} className="w-full aspect-video" allow="autoplay; fullscreen" allowFullScreen />
                  : <video src={getImageUrl(item.url)} className="w-full block" controls autoPlay />
            ) : (
              <>
                {isYouTube
                  ? <img src={getYouTubeThumbnail(item.url)} alt={item.title} className="w-full block transition-all duration-500 group-hover:brightness-75" />
                  : isVimeo
                    ? <div className="aspect-video bg-[#1a1a1a] flex items-center justify-center"><span className="text-white/40 text-xs">Vimeo</span></div>
                    : <video src={`${getImageUrl(item.url)}#t=0.001`} className="w-full block" preload="metadata" />}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                  onClick={() => setPlayingVideoId(item.item_id || idx)}>
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
                    <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                  </div>
                </div>
                <div className="absolute top-2.5 right-2.5 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-md flex items-center gap-1">
                  ▶ Video
                </div>
              </>
            )}
          </div>
        ) : (
          <img src={getImageUrl(item.url)} alt={item.title}
            className="w-full block transition-all duration-500 group-hover:scale-[1.04] group-hover:brightness-[0.8]" />
        )}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(14,14,14,0.6) 0%, transparent 50%)' }}>
          {item.title && (
            <span className="text-[11px] font-semibold text-white bg-black/50 rounded-full px-2.5 py-1 backdrop-blur-sm">
              {item.title}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-full">

      {/* ── Booking Dialog ── */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Book {profile.user_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedPackage ? (
              <>
                <div>
                  <Label>Location *</Label>
                  <Input value={bookingData.location} onChange={(e) => setBookingData({ ...bookingData, location: e.target.value })} placeholder="e.g., 123 Main St, Los Angeles, CA" className="mt-1" data-testid="booking-location-input" />
                </div>
                <div>
                  <Label>Shoot Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1" data-testid="booking-date-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {bookingData.date ? format(bookingData.date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={bookingData.date} onSelect={(date) => setBookingData({ ...bookingData, date })} disabled={(date) => date < new Date() || isDateUnavailable(date)} modifiers={{ unavailable: (date) => isDateUnavailable(date) }} modifiersStyles={{ unavailable: { textDecoration: 'line-through', color: '#f87171' } }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Delivery Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {bookingData.delivery_date ? format(bookingData.delivery_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={bookingData.delivery_date} onSelect={(delivery_date) => setBookingData({ ...bookingData, delivery_date })} disabled={(date) => date < new Date()} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Time</Label>
                  <select value={bookingData.time} onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })} onWheel={(e) => e.stopPropagation()} className="mt-1 w-full h-11 px-4 border border-[#e4e1db] rounded-[14px] focus:border-ink focus:outline-none bg-white text-sm">
                    {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00'].map((time) => {
                      const unavailable = isTimeUnavailable(bookingData.date, time);
                      return <option key={time} value={time} disabled={unavailable}>{time}{unavailable ? ' (Unavailable)' : ''}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <Label>Notes for photographer</Label>
                  <Textarea value={bookingData.project_description} onChange={(e) => setBookingData({ ...bookingData, project_description: e.target.value })} placeholder="Anything the photographer should know..." className="mt-1" rows={2} data-testid="booking-description-input" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Project Title *</Label>
                  <Input value={bookingData.project_title} onChange={(e) => setBookingData({ ...bookingData, project_title: e.target.value })} placeholder="e.g., Product Photoshoot" className="mt-1" data-testid="booking-title-input" />
                </div>
                <div>
                  <Label>Project Description *</Label>
                  <Textarea value={bookingData.project_description} onChange={(e) => setBookingData({ ...bookingData, project_description: e.target.value })} placeholder="Describe your project in detail..." className="mt-1" rows={2} data-testid="booking-description-input" />
                </div>
                <div>
                  <Label>Expected Deliverables *</Label>
                  <Textarea value={bookingData.deliverables} onChange={(e) => setBookingData({ ...bookingData, deliverables: e.target.value })} placeholder="e.g., 20 edited photos, raw files..." className="mt-1" rows={2} data-testid="booking-deliverables-input" />
                </div>
                <div>
                  <Label>Location *</Label>
                  <Input value={bookingData.location} onChange={(e) => setBookingData({ ...bookingData, location: e.target.value })} placeholder="e.g., 123 Main St, Los Angeles, CA" className="mt-1" data-testid="booking-location-input" />
                </div>
                <div>
                  <Label>Shoot Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1" data-testid="booking-date-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {bookingData.date ? format(bookingData.date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={bookingData.date} onSelect={(date) => setBookingData({ ...bookingData, date })} disabled={(date) => date < new Date() || isDateUnavailable(date)} modifiers={{ unavailable: (date) => isDateUnavailable(date) }} modifiersStyles={{ unavailable: { textDecoration: 'line-through', color: '#f87171' } }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Delivery Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {bookingData.delivery_date ? format(bookingData.delivery_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={bookingData.delivery_date} onSelect={(delivery_date) => setBookingData({ ...bookingData, delivery_date })} disabled={(date) => date < new Date()} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Time *</Label>
                    <select value={bookingData.time} onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })} onWheel={(e) => e.stopPropagation()} className="mt-1 w-full h-11 px-4 border border-[#e4e1db] rounded-[14px] focus:border-ink focus:outline-none bg-white text-sm">
                      {['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00','00:00'].map((time) => {
                        const unavailable = isTimeUnavailable(bookingData.date, time);
                        return <option key={time} value={time} disabled={unavailable}>{time}{unavailable ? ' (Unavailable)' : ''}</option>;
                      })}
                    </select>
                  </div>
                  <div>
                    <Label>Duration (hours) *</Label>
                    <Input type="number" min="1" max="12" value={bookingData.duration_hours} onChange={(e) => setBookingData({ ...bookingData, duration_hours: Number(e.target.value) })} className="mt-1" />
                  </div>
                </div>
              </>
            )}

            {/* Price summary */}
            <div className="p-4 rounded-[14px] bg-[#f7f5f2] border border-[#e4e1db]">
              {selectedPackage ? (
                <>
                  <div className="flex justify-between mb-1">
                    <span className="text-[#5a5a5a] font-medium text-sm">{selectedPackage.name}</span>
                    <span className="font-semibold text-sm">${selectedPackage.price.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-[#a0a0a0] mb-1">{selectedPackage.duration_hours}h · {selectedPackage.revisions_included} revisions</p>
                  <p className="text-xs text-[#5a5a5a] mb-2">Includes: {selectedPackage.deliverables}</p>
                  <div className="flex justify-between text-sm text-[#5a5a5a] border-t border-[#e4e1db] pt-2 mt-2">
                    <span><s>Platform fee (15%)</s></span>
                    <span className="text-right"><s className="text-[#a0a0a0]">${(selectedPackage.price * 0.15).toFixed(2)}</s><br/><span className="text-green-600 font-medium">Free Beta</span></span>
                  </div>
                  <div className="flex justify-between text-base font-bold mt-1">
                    <span>Total</span><span>${(selectedPackage.price * 1.0).toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-[#5a5a5a]">${profile.hourly_rate} × {bookingData.duration_hours} hours</span>
                    <span className="font-semibold">${totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-[#5a5a5a] mb-1">
                    <span><s>Platform fee (15%)</s></span>
                    <span className="text-right"><s className="text-[#a0a0a0]">${(totalAmount * 0.15).toFixed(2)}</s><br/><span className="text-green-600 font-medium">Free Beta</span></span>
                  </div>
                  <div className="flex justify-between text-base font-bold border-t border-[#e4e1db] pt-2">
                    <span>Total</span><span>${(totalAmount * 1.0).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            <Button onClick={handleBooking} className="w-full h-12" disabled={bookingLoading} data-testid="confirm-booking-btn">
              {bookingLoading ? 'Processing...' : 'Request Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Hero Banner ── */}
      <div className="relative flex-shrink-0 overflow-hidden bg-ink h-[200px] sm:h-[260px] md:h-[320px]">
        {profile.banner_image ? (
          <img src={getImageUrl(profile.banner_image)} alt="" className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.55) saturate(0.8)', transition: 'transform 8s ease' }} />
        ) : profile.user_picture ? (
          <img src={getImageUrl(profile.user_picture)} alt="" className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'blur(8px) brightness(0.45)', transform: 'scale(1.1)' }} />
        ) : (
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #1a1a1a, #2a2a2a)' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,14,14,0.85) 0%, rgba(14,14,14,0.2) 50%, transparent 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 px-10 pb-8 flex items-end justify-between gap-5">
          <div>
            {profile.city && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.5px] mb-2.5"
                style={{ color: 'rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                <MapPin className="w-2.5 h-2.5" />{profile.city}
              </div>
            )}
            <h1 className="font-display text-white leading-[1.05] mb-1.5"
              style={{ fontSize: 'clamp(32px, 4vw, 48px)', letterSpacing: '-1px' }}>
              {profile.user_name}
            </h1>
            {profile.title && <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>{profile.title}</p>}
            <div className="flex items-center gap-2.5">
              <button onClick={openHourlyBooking} data-testid="book-now-btn"
                className="inline-flex items-center gap-1.5 bg-white text-ink text-sm font-semibold px-6 rounded-full transition-all"
                style={{ padding: '11px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.3)' }}
                onMouseOver={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseOut={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.3)'; e.currentTarget.style.transform = 'none'; }}>
                Book Now
              </button>
              <button onClick={handleMessage} data-testid="message-btn"
                className="inline-flex items-center gap-1.5 text-white text-sm font-medium rounded-full transition-all"
                style={{ padding: '11px 24px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(8px)' }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}>
                <MessageCircle className="w-3.5 h-3.5" />Message
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky Tab Bar ── */}
      <div className="flex-shrink-0 bg-white sticky top-0 z-10 border-b border-[#e4e1db]"
        style={{ boxShadow: '0 2px 12px rgba(14,14,14,0.04)' }}>
        <div className="px-4 md:px-10 flex items-center justify-between overflow-x-auto">
          <div className="flex">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'portfolio', label: 'Portfolio' },
              { key: 'pricing', label: 'Pricing' },
            ].map((tab) => (
              <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPlayingVideoId(null); }}
                className={`text-sm font-medium px-5 py-4 border-b-2 -mb-px transition-all ${
                  activeTab === tab.key
                    ? 'border-ink text-ink font-semibold'
                    : 'border-transparent text-[#a0a0a0] hover:text-ink'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>
          {profile.total_reviews > 0 && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-[#5a5a5a] py-3">
              <span className="text-[#d4a843] tracking-[-1px]">{'★'.repeat(Math.round(profile.rating || 0))}</span>
              <span className="font-semibold text-ink">{profile.rating?.toFixed(1)}</span>
              <span className="text-[#a0a0a0] text-xs">({profile.total_reviews} {profile.total_reviews === 1 ? 'review' : 'reviews'})</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="flex-1 overflow-y-auto">

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_380px]">

            {/* Featured Work */}
            <div className="px-4 py-6 md:px-10 md:py-9 border-b md:border-b-0 md:border-r border-[#e4e1db]">
              <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-5">Featured work</p>
              {featuredItems.length > 0 ? (
                <>
                  {featuredTags.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-4">
                      {['all', ...featuredTags].map(cat => (
                        <button key={cat} onClick={() => setFeaturedFilter(cat)}
                          className={`px-3 py-1 rounded-full text-[11px] font-medium transition-all ${
                            featuredFilter === cat
                              ? 'bg-ink text-white border border-ink'
                              : 'bg-white border border-[#e4e1db] text-[#5a5a5a] hover:border-ink hover:text-ink'
                          }`}>
                          {cat === 'all' ? 'All' : cat}
                        </button>
                      ))}
                    </div>
                  )}
                  {filteredFeaturedItems.length > 0 ? (
                    <div className="columns-2 sm:columns-3 gap-2">
                      {filteredFeaturedItems.slice(0, 5).map((item, idx) => renderPortfolioItem(item, idx, false))}
                    </div>
                  ) : (
                    <p className="text-[#a0a0a0] text-sm py-8 text-center">No featured work with this tag</p>
                  )}
                </>
              ) : (
                <div className="text-center py-12 bg-[#f7f5f2] rounded-[14px] border border-[#e4e1db]">
                  <Star className="w-10 h-10 mx-auto text-[#e4e1db] mb-3" />
                  <p className="text-[#a0a0a0] text-sm">No featured work yet</p>
                  {isOwner && <p className="text-[#a0a0a0] text-xs mt-1">Star items in the Portfolio tab to feature them here</p>}
                </div>
              )}
            </div>

            {/* Profile Sidebar */}
            <div className="px-4 py-6 md:px-9 md:py-10 bg-white flex flex-col gap-7">

              {/* About */}
              <div>
                <p className="text-[11px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3.5">About</p>
                <p className="text-[15px] text-[#5a5a5a] leading-[1.75]">{profile.bio || 'No bio provided yet.'}</p>
              </div>

              <div className="h-px bg-[#e4e1db]" />

              {/* Details */}
              <div>
                <p className="text-[11px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3.5">Details</p>
                {profile.city && (
                  <div className="flex items-center gap-2.5 text-sm text-[#5a5a5a] mb-2.5">
                    <MapPin className="w-3.5 h-3.5 text-[#a0a0a0] flex-shrink-0" />{profile.city}
                  </div>
                )}
                {profile.created_at && (
                  <div className="flex items-center gap-2.5 text-sm text-[#5a5a5a]">
                    <CalendarIcon className="w-3.5 h-3.5 text-[#a0a0a0] flex-shrink-0" />
                    Member since {new Date(profile.created_at).getFullYear()}
                  </div>
                )}
              </div>

              {profile.specialties?.length > 0 && <div className="h-px bg-[#e4e1db]" />}

              {/* Specialties */}
              {profile.specialties?.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3.5">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.specialties.map((s, idx) => (
                      <span key={idx} className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-[#f7f5f2] border border-[#e4e1db] text-[#5a5a5a] hover:border-ink hover:text-ink transition-colors cursor-default">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile.total_reviews > 0 && <div className="h-px bg-[#e4e1db]" />}

              {/* Rating */}
              {profile.total_reviews > 0 && (
                <div>
                  <p className="text-[11px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-3.5">Rating</p>
                  <div className="flex items-center gap-2.5">
                    <span className="text-[#d4a843] text-xl tracking-[-1px]">{'★'.repeat(Math.round(profile.rating || 0))}</span>
                    <span className="text-xl font-bold text-ink">{profile.rating?.toFixed(1)}</span>
                    <span className="text-sm text-[#a0a0a0]">/ {profile.total_reviews} {profile.total_reviews === 1 ? 'review' : 'reviews'}</span>
                  </div>
                </div>
              )}

              {/* Jobs completed */}
              {profile.total_bookings > 0 && (
                <div className="flex items-center gap-2.5 px-4 py-3.5 bg-[#f7f5f2] border border-[#e4e1db] rounded-[14px] text-sm font-medium text-[#5a5a5a]">
                  <CheckCircle className="w-4 h-4 text-[#4ade80] flex-shrink-0" />
                  <span><span className="font-bold text-ink">{profile.total_bookings}</span> completed job{profile.total_bookings !== 1 ? 's' : ''} on FrameFinder</span>
                </div>
              )}

              {/* Badges */}
              {profile.badges?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profile.badges.includes('top_rated') && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold bg-[#f5ead0] text-[#8a6a1a] border border-[#e8d49a]">
                      ◆ Premier
                    </span>
                  )}
                  {profile.badges.includes('trending') && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#eff6ff] text-[#1e40af] border border-[#bfdbfe]">
                      <TrendingUp className="w-3.5 h-3.5" />Trending
                    </span>
                  )}
                  {profile.badges.includes('verified') && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0]">
                      <BadgeCheck className="w-3.5 h-3.5" />Verified
                    </span>
                  )}
                  {profile.badges.includes('featured') && (
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-ink text-white">
                      <Award className="w-3.5 h-3.5" />Featured
                    </span>
                  )}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-col gap-2 pt-1">
                <button onClick={openHourlyBooking}
                  className="flex items-center justify-center gap-2 w-full bg-ink text-white text-[15px] font-semibold rounded-full transition-opacity hover:opacity-80"
                  style={{ padding: '15px' }}>
                  Book Now →
                </button>
                <button onClick={handleMessage}
                  className="flex items-center justify-center gap-2 w-full text-ink text-sm font-medium rounded-full transition-all hover:bg-[#e4e1db]"
                  style={{ padding: '13px', background: 'transparent', border: '1px solid #e4e1db' }}>
                  <MessageCircle className="w-3.5 h-3.5" />Send a Message
                </button>
              </div>

            </div>
          </div>
        )}

        {/* PORTFOLIO */}
        {activeTab === 'portfolio' && (
          <div className="px-4 py-6 md:px-10 md:py-9">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              {portfolioTags.length > 0 ? (
                <div className="flex gap-2 flex-wrap">
                  {['all', ...portfolioTags].map(cat => (
                    <button key={cat} onClick={() => setPortfolioFilter(cat)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                        portfolioFilter === cat
                          ? 'bg-ink text-white border border-ink'
                          : 'bg-white border border-[#e4e1db] text-[#5a5a5a] hover:border-ink hover:text-ink'
                      }`}>
                      {cat === 'all' ? 'All' : cat}
                    </button>
                  ))}
                </div>
              ) : <span />}
              <span className="text-sm text-[#a0a0a0]">{filteredPortfolioItems.length} work{filteredPortfolioItems.length !== 1 ? 's' : ''}</span>
            </div>
            {filteredPortfolioItems.length > 0 ? (
              <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 gap-2">
                {filteredPortfolioItems.map((item, idx) => renderPortfolioItem(item, idx, isOwner))}
              </div>
            ) : (
              <div className="text-center py-12 bg-[#f7f5f2] rounded-[14px] border border-[#e4e1db]">
                <Camera className="w-10 h-10 mx-auto text-[#e4e1db] mb-3" />
                <p className="text-[#a0a0a0] text-sm">No portfolio items yet</p>
              </div>
            )}
          </div>
        )}

        {/* PRICING */}
        {activeTab === 'pricing' && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 px-4 py-6 md:px-10 md:py-9">
            <div>

              {/* Hourly rate hero */}
              {profile.hourly_rate > 0 && (
                <div className="bg-ink rounded-[24px] px-9 py-8 flex items-center justify-between mb-7 relative overflow-hidden">
                  <div className="absolute right-[-60px] top-[-60px] w-[280px] h-[280px] rounded-full pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(212,168,67,0.12) 0%, transparent 70%)' }} />
                  <div className="relative z-10">
                    <p className="text-[11px] font-bold tracking-[1.5px] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>Standard Rate</p>
                    <p className="font-display text-white text-[22px]" style={{ letterSpacing: '-0.3px' }}>Hourly Booking</p>
                    <p className="text-sm mt-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Flexible bookings for shoots of any size</p>
                  </div>
                  <div className="text-right relative z-10">
                    <p className="font-display text-white" style={{ fontSize: '52px', letterSpacing: '-2px', lineHeight: 1 }}>
                      ${profile.hourly_rate}
                    </p>
                    <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>per hour</p>
                  </div>
                </div>
              )}

              {/* Packages */}
              {servicePackages.length > 0 && (
                <div className="mb-8">
                  <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0] mb-4">Packages</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {servicePackages.map((pkg) => (
                      <div key={pkg.package_id}
                        className="bg-white border border-[#e4e1db] rounded-[24px] p-6 flex flex-col transition-all hover:border-ink hover:-translate-y-0.5"
                        style={{ boxShadow: 'none', transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s' }}
                        onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(14,14,14,0.08)'}
                        onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}
                        data-testid={`package-card-${pkg.package_id}`}>
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-[#0e0e0e]">
                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                              <path d="M10 2l7 4v8l-7 4-7-4V6l7-4z"/><path d="M10 2v14M3 6l7 4 7-4"/>
                            </svg>
                          </span>
                          <span className="font-display text-ink" style={{ fontSize: '28px', letterSpacing: '-1px', lineHeight: 1 }}>
                            ${pkg.price.toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[15px] font-semibold text-ink mb-1.5">{pkg.name}</p>
                        <p className="text-xs text-[#a0a0a0] mb-4">{pkg.description}</p>
                        <div className="flex gap-3.5 mb-3.5">
                          <span className="flex items-center gap-1.5 text-xs text-[#a0a0a0]"><Clock className="w-3 h-3" />{pkg.duration_hours}h</span>
                          <span className="flex items-center gap-1.5 text-xs text-[#a0a0a0]">{pkg.revisions_included} revisions</span>
                        </div>
                        <p className="text-sm text-[#5a5a5a] leading-[1.6] flex-1 pb-4 mb-4 border-b border-[#e4e1db]">{pkg.deliverables}</p>
                        <button onClick={() => selectPackage(pkg)}
                          className="flex items-center justify-center w-full bg-ink text-white text-sm font-semibold rounded-full transition-opacity hover:opacity-80"
                          style={{ padding: '11px' }}>
                          Select Package →
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reviews */}
              {profile.reviews?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[10px] font-bold tracking-[1.8px] uppercase text-[#a0a0a0]">Client Reviews</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#d4a843] text-sm">{'★'.repeat(Math.round(profile.rating || 0))}</span>
                      <span className="text-[15px] font-bold text-ink">{profile.rating?.toFixed(1)}</span>
                      <span className="text-xs text-[#a0a0a0]">({profile.total_reviews} {profile.total_reviews === 1 ? 'review' : 'reviews'})</span>
                    </div>
                  </div>
                  {profile.reviews.map((review, idx) => (
                    <div key={idx} className="py-4 border-b border-[#e4e1db] last:border-0">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="w-8 h-8 rounded-full bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center text-xs font-semibold text-ink flex-shrink-0">
                          {review.business_name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-ink">{review.business_name}</p>
                          <p className="text-[11px] text-[#a0a0a0]">{new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        </div>
                      </div>
                      <span className="text-[#d4a843] text-sm block mb-1.5">{'★'.repeat(review.rating)}</span>
                      <p className="text-sm text-[#5a5a5a] leading-[1.65]">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing aside */}
            <div>
              <div className="bg-white border border-[#e4e1db] rounded-[24px] p-6 sticky top-[70px]">
                <p className="text-sm font-semibold text-ink mb-4">Why book through FrameFinder</p>
                {[
                  {
                    icon: (
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <rect x="4" y="9" width="12" height="9" rx="1.5"/><path d="M7 9V6a3 3 0 016 0v3"/>
                      </svg>
                    ),
                    label: 'Secure escrow', desc: 'Funds held until you approve deliverables'
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M5 3h8l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M13 3v4h3"/><path d="M7 9h6M7 12h4"/>
                      </svg>
                    ),
                    label: 'Built-in contract', desc: 'Scope, timeline, and revisions in writing'
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <circle cx="10" cy="10" r="7"/><path d="M10 7v3l2 2"/>
                      </svg>
                    ),
                    label: 'Dispute resolution', desc: 'Platform support if anything goes wrong'
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                        <path d="M3 7a2 2 0 012-2h3l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/>
                      </svg>
                    ),
                    label: 'Organised delivery', desc: 'All files, revisions, and history in one place'
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 py-2.5 border-b border-[#e4e1db] last:border-0 text-sm">
                    <span className="flex-shrink-0 mt-0.5 text-[#0e0e0e]">{item.icon}</span>
                    <div>
                      <p className="font-medium text-ink">{item.label}</p>
                      <p className="text-xs text-[#a0a0a0] mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <button onClick={openHourlyBooking}
                    className="flex items-center justify-center gap-1.5 w-full bg-ink text-white text-sm font-semibold rounded-full transition-opacity hover:opacity-80"
                    style={{ padding: '12px' }}>
                    Book Now →
                  </button>
                  <button onClick={handleMessage}
                    className="flex items-center justify-center gap-1.5 w-full text-ink text-sm font-medium rounded-full transition-all hover:bg-[#e4e1db]"
                    style={{ padding: '10px', border: '1px solid #e4e1db' }}>
                    <MessageCircle className="w-3 h-3" />Send a Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Image / Video Modal ── */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          {selectedImage.isEmbedVideo ? (
            <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
              <iframe src={selectedImage.embedUrl} className="w-full h-full" frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title={selectedImage.title} />
            </div>
          ) : selectedImage.type === 'video' ? (
            <video src={getImageUrl(selectedImage.url)} className="max-w-full max-h-full" controls autoPlay onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={getImageUrl(selectedImage.url)} alt={selectedImage.title} className="max-w-full max-h-full object-contain" />
          )}
        </div>
      )}
    </div>
  );
};

export default CreativeProfile;
